import LookerReportType from '@/Enums/Looker/ReportType.enum';
import { BeeswaxReportProperties } from '@/Enums/Report/BeeswaxReportProperties.enum';
import type { IBaseDataReport, IDateDataReport } from '@/Interfaces/Attribution/Report.interface';
import type {
  ICampaignNameReportRecord,
  IChannelReportRecord,
  ICreativeReportRecord,
  IDayOfWeekReportRecord,
  IDaypartReportRecord,
  IDeviceReportRecord,
  IPixelReportRecord,
  IPlacementReportRecord,
  IPublisherReportRecord,
} from '@/Interfaces/Attribution/ReportRecord.interface';
import type { IWebVisitsState } from '@/Interfaces/Attribution/State.interface';
import type {
  ICityReportRecord,
  ICongressionalDistrictsReportRecord,
  ICountyReportRecord,
  IDmaReportRecord,
  IStateReportRecord,
  IZipReportRecord,
} from '@/Interfaces/ReportingDashboard/Beeswax/ReportRecord.interface';
import type { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import { attributionRequestHandler } from '@/helpers/attribution/beeswax';
import type { RequestMap } from '@/types/RequestMap.types';
import { createAsyncThunk } from '@reduxjs/toolkit';

enum ActionPrefix {
  FETCH_DATE_DATA = 'webVisits/fetchDateData',
  FETCH_DEVICE_DATA = 'webVisits/fetchDeviceData',
  FETCH_DIGITAL_CHANNEL_DATA = 'webVisits/fetchDigitalChannelData',
  FETCH_CAMPAIGN_NAME_DATA = 'webVisits/fetchCampaignNameData',
  FETCH_CREATIVE_DATA = 'webVisits/fetchCreativeData',
  FETCH_PUBLISHER_NAME_DATA = 'webVisits/fetchPublisherNameData',
  FETCH_DAYPART_DATA = 'webVisits/fetchDaypartData',
  FETCH_DAY_OF_WEEK_DATA = 'webVisits/fetchDayOfWeekData',
  FETCH_PLACEMENT_DATA = 'webVisits/fetchPlacementData',
  FETCH_PIXEL_DATA = 'webVisits/fetchPixelData',
  FETCH_STATE_DATA = 'webVisits/fetchStateData',
  FETCH_COUNTY_DATA = 'webVisits/fetchCountyData',
  FETCH_ZIP_DATA = 'webVisits/fetchZipData',
  FETCH_CONGRESSIONAL_DISTINCTS_DATA = 'webVisits/fetchCongressionalDistinctsData',
  FETCH_DMA_DATA = 'webVisits/fetchDmaData',
  FETCH_CITY_DATA = 'webVisits/fetchCityData',
}

export const getWebVisitsDateData = createAsyncThunk<IDateDataReport, IAbortSignalThunkArg>(
  ActionPrefix.FETCH_DATE_DATA,
  attributionRequestHandler<IDateDataReport>(LookerReportType.DateWebVisits, {
    report: [],
    totals: { [BeeswaxReportProperties.IMPRESSIONS]: 0 },
  }),
);

export const getWebVisitsDeviceData = createAsyncThunk<IBaseDataReport<IDeviceReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.FETCH_DEVICE_DATA,
  attributionRequestHandler(LookerReportType.DeviceWebVisits),
);

export const getWebVisitsDigitalChannelData = createAsyncThunk<
  IBaseDataReport<IChannelReportRecord>,
  IAbortSignalThunkArg
>(ActionPrefix.FETCH_DIGITAL_CHANNEL_DATA, attributionRequestHandler(LookerReportType.DigitalChannelWebVisits));

export const getWebVisitsCampaignNameData = createAsyncThunk<
  IBaseDataReport<ICampaignNameReportRecord>,
  IAbortSignalThunkArg
>(ActionPrefix.FETCH_CAMPAIGN_NAME_DATA, attributionRequestHandler(LookerReportType.CampaignWebVisits));

export const getWebVisitsCreativeData = createAsyncThunk<IBaseDataReport<ICreativeReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.FETCH_CREATIVE_DATA,
  attributionRequestHandler(LookerReportType.CreativeWebVisits),
);

export const getWebVisitsPublisherNameData = createAsyncThunk<
  IBaseDataReport<IPublisherReportRecord>,
  IAbortSignalThunkArg
>(ActionPrefix.FETCH_PUBLISHER_NAME_DATA, attributionRequestHandler(LookerReportType.PublisherWebVisits));

export const getWebVisitsDaypartData = createAsyncThunk<IBaseDataReport<IDaypartReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.FETCH_DAYPART_DATA,
  attributionRequestHandler(LookerReportType.DaypartWebVisits),
);

export const getWebVisitsDayOfWeekData = createAsyncThunk<
  IBaseDataReport<IDayOfWeekReportRecord>,
  IAbortSignalThunkArg
>(ActionPrefix.FETCH_DAY_OF_WEEK_DATA, attributionRequestHandler(LookerReportType.DayOfWeekWebVisits));

export const getWebVisitsPlacementData = createAsyncThunk<
  IBaseDataReport<IPlacementReportRecord>,
  IAbortSignalThunkArg
>(ActionPrefix.FETCH_PLACEMENT_DATA, attributionRequestHandler(LookerReportType.PlacementWebVisits));

export const getWebVisitsPixelData = createAsyncThunk<IBaseDataReport<IPixelReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.FETCH_PIXEL_DATA,
  attributionRequestHandler(LookerReportType.PixelWebVisits),
);

export const getWebVisitsStateData = createAsyncThunk<IBaseDataReport<IStateReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.FETCH_STATE_DATA,
  attributionRequestHandler(LookerReportType.StateWebVisits),
);

export const getWebVisitsCountyData = createAsyncThunk<IBaseDataReport<ICountyReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.FETCH_COUNTY_DATA,
  attributionRequestHandler(LookerReportType.CountyWebVisits),
);

export const getWebVisitsZipData = createAsyncThunk<IBaseDataReport<IZipReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.FETCH_ZIP_DATA,
  attributionRequestHandler(LookerReportType.ZipWebVisits),
);

export const getWebVisitsCongressionalDistrictsData = createAsyncThunk<
  IBaseDataReport<ICongressionalDistrictsReportRecord>,
  IAbortSignalThunkArg
>(
  ActionPrefix.FETCH_CONGRESSIONAL_DISTINCTS_DATA,
  attributionRequestHandler(LookerReportType.CongressionalDistrictWebVisits),
);

export const getWebVisitsDmaData = createAsyncThunk<IBaseDataReport<IDmaReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.FETCH_DMA_DATA,
  attributionRequestHandler(LookerReportType.DmaWebVisits),
);

export const getWebVisitsCityData = createAsyncThunk<IBaseDataReport<ICityReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.FETCH_CITY_DATA,
  attributionRequestHandler(LookerReportType.CityWebVisits),
);

export const webVisitsReportMap: RequestMap<IWebVisitsState> = [
  ['deviceData', getWebVisitsDeviceData],
  ['channelData', getWebVisitsDigitalChannelData],
  ['creativeData', getWebVisitsCreativeData],
  ['publisherNameData', getWebVisitsPublisherNameData],
  ['dayTimeData', getWebVisitsDaypartData],
  ['dayOfWeekData', getWebVisitsDayOfWeekData],
  ['stateData', getWebVisitsStateData],
  ['dmaData', getWebVisitsDmaData],
  ['countyData', getWebVisitsCountyData],
  ['zipData', getWebVisitsZipData],
  ['congressionalDistrictsData', getWebVisitsCongressionalDistrictsData],
  ['cityData', getWebVisitsCityData],
];
